<template>
  <v-card class="pa-4" max-width="900">
    <v-card-title>{{ props.title }}</v-card-title>
    <v-progress-linear
      v-if="loadingCatalog || session?.busy.value"
      indeterminate
    />
    <v-alert v-if="error" type="error" class="my-3">{{ error }}</v-alert>
    <v-alert v-if="session?.busy.value" type="info" class="my-3">
      Your member cart is loading. Please wait before adding items.
    </v-alert>
    <v-alert
      v-if="selected?.itemType === ItemType.DONATION"
      type="warning"
      class="my-3"
    >
      Donation fund and initiative details are not supported by the member order
      API yet.
    </v-alert>
    <v-alert
      v-if="selected?.itemType === ItemType.PAID_CLASS"
      type="info"
      class="my-3"
    >
      Add paid class enrollments from the Classes page so your signup is
      attached correctly.
      <v-btn class="ml-2" variant="text" to="/class-signup"
        >Browse classes</v-btn
      >
    </v-alert>
    <v-alert
      v-if="selected?.itemType === ItemType.GUEST_PRIVS && !props.membershipId"
      type="info"
      class="my-3"
    >
      Add guest privileges from the membership that owns them.
    </v-alert>
    <v-card-text>
      <p v-if="restocking && props.balanceNumber" class="mb-4">
        Add funds to balance {{ props.balanceNumber }}.
      </p>
      <v-select
        v-if="!restocking"
        v-model="selected"
        :items="items"
        :item-title="itemTitle"
        return-object
        label="Catalog item"
        :disabled="loadingCatalog || !session?.canEdit.value"
      />
      <v-select
        v-if="selected?.hasPerson() && !restocking"
        v-model="recipientId"
        :items="recipients"
        item-title="title"
        item-value="personId"
        label="Recipient"
        :disabled="!session?.isOpen.value"
      />
      <v-text-field
        v-if="selected?.itemType === ItemType.PRODUCT"
        v-model.number="quantity"
        label="Quantity"
        type="number"
        :min="selected.catalogItemInstance.minQuantity ?? 1"
        :max="selected.catalogItemInstance.maxQuantity ?? undefined"
      />
      <v-text-field
        v-if="selected?.itemType === ItemType.CARD"
        v-model="cardNumber"
        label="Card number (optional)"
        hint="Leave blank to assign the next available card number."
        persistent-hint
      />
      <v-text-field
        v-if="selected?.itemType === ItemType.GIFT_CARD && !restocking"
        v-model="balanceNumber"
        label="Existing balance number (optional)"
        hint="Leave blank to issue a new gift card."
        persistent-hint
      />
      <CurrencyInput
        v-if="selected?.catalogItemInstance.allowPriceOverride === true"
        v-model:amount-cents="specifiedAmountCents"
        label="Amount"
        :required="restocking"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn :disabled="session?.busy.value" @click="emit('close')"
        >Close</v-btn
      >
      <v-btn
        color="primary"
        :disabled="!canAdd"
        :loading="session?.busy.value"
        @click="addToCart"
      >
        Add to cart
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import { BalanceType } from "../../../cbi-generator/generated/dto/BalanceType";
import type { PersonRef } from "../../../cbi-generator/generated/dto/PersonRef";
import type { ProductListing } from "@/api/models/shop/ProductListing";
import { useCatalog } from "@/composables/shop/useCatalog";
import { buildMemberCatalogPurchase } from "@/composables/shop/memberCatalogPurchase";
import {
  memberOrderSessionKey,
  type MemberOrderSession,
} from "@/composables/shop/useMemberOrder";
import { useNotificationStore } from "@/stores/notification";
import { useUser } from "@/composables/useUser";

const props = withDefaults(
  defineProps<{
    title: string;
    itemType?: ItemType;
    membershipId?: number;
    /** Reloads an existing balance: the matching gift-card item is chosen automatically and
     * only the amount is asked for. */
    restockBalanceType?: BalanceType;
    balanceNumber?: string;
  }>(),
  {
    itemType: undefined,
    membershipId: undefined,
    restockBalanceType: undefined,
    balanceNumber: undefined,
  },
);
const restocking = computed(() => props.restockBalanceType != null);
const emit = defineEmits<{ close: [] }>();
const session = inject<MemberOrderSession | null>(memberOrderSessionKey, null);
const notification = useNotificationStore();
const { currentPersonRef: person, currentChildrenRef: children } = useUser();
const loadingCatalog = ref(true);
const products = shallowRef<ProductListing[]>([]);
const selected = shallowRef<ProductListing | null>(null);
const recipientId = ref<number | null>(person.value?.personId ?? null);
const quantity = ref(1);
const cardNumber = ref("");
const balanceNumber = ref(props.balanceNumber ?? "");
const specifiedAmountCents = ref<number>();
const error = ref("");

watch(
  () => person.value?.personId,
  (personId, previousId) => {
    if (recipientId.value == null || recipientId.value === previousId)
      recipientId.value = personId ?? null;
  },
);
watch(selected, () => {
  quantity.value = 1;
  specifiedAmountCents.value = undefined;
});

const recipients = computed(() => {
  const current = person.value;
  if (!current) return [];
  return [current, ...children.value].map((member) => ({
    personId: member.personId,
    title:
      [member.first, member.last].filter(Boolean).join(" ") ||
      `Person #${member.personId}`,
  }));
});
const items = computed(() =>
  products.value.filter((item) =>
    props.restockBalanceType != null
      ? item.itemType === ItemType.GIFT_CARD &&
        (item.catalogItem.balanceType ?? BalanceType.GIFT_CARD) ===
          props.restockBalanceType
      : !props.itemType || item.itemType === props.itemType,
  ),
);
const itemTitle = (item: ProductListing) =>
  `${item.itemName ?? "Catalog item"} — ${new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format((item.catalogItemInstance.priceCents ?? 0) / 100)}`;
const canAdd = computed(() => {
  if (!session?.canEdit.value || !selected.value) return false;
  if (
    restocking.value &&
    selected.value.allowPriceOverride &&
    !((specifiedAmountCents.value ?? 0) > 0)
  )
    return false;
  if (
    selected.value.itemType === ItemType.DONATION ||
    selected.value.itemType === ItemType.PAID_CLASS
  )
    return false;
  if (
    selected.value.itemType === ItemType.GUEST_PRIVS &&
    props.membershipId == null
  )
    return false;
  if (
    selected.value.hasPerson() &&
    (recipientId.value == null ||
      !recipients.value.some(
        (recipient) => recipient.personId === recipientId.value,
      ))
  )
    return false;
  return true;
});

onMounted(async () => {
  try {
    products.value = (await useCatalog()).products;
    if (props.restockBalanceType != null) {
      const label = props.restockBalanceType.replaceAll("_", " ").toLowerCase();
      if (items.value.length === 1) selected.value = items.value[0];
      else
        error.value =
          items.value.length === 0
            ? `No catalog item can add funds to a ${label} balance right now.`
            : `More than one catalog item can add funds to a ${label} balance. Please contact the front desk.`;
    }
  } catch (failure) {
    error.value =
      failure instanceof Error
        ? failure.message
        : "Unable to load the catalog.";
  } finally {
    loadingCatalog.value = false;
  }
});

async function addToCart() {
  if (!session || !selected.value || !canAdd.value) return;
  error.value = "";
  try {
    const recipient: PersonRef | null =
      recipientId.value == null ? null : { personId: recipientId.value };
    await session.add(
      buildMemberCatalogPurchase(selected.value, {
        recipient,
        quantity: quantity.value,
        specifiedAmountCents: specifiedAmountCents.value ?? null,
        cardNum: cardNumber.value,
        balanceNumber: balanceNumber.value,
        membership:
          props.membershipId == null ? null : { assignId: props.membershipId },
      }),
    );
    notification.addSnackbar("Added to your cart.", "success");
    emit("close");
  } catch (failure) {
    error.value =
      failure instanceof Error ? failure.message : "Unable to add this item.";
  }
}
</script>
